import cloneDeep from 'lodash/cloneDeep';
import trim from 'lodash/trim';
import { action, extendObservable } from 'mobx';
import { UiService } from 'modules/ui';
import {
  DeepSearchService,
  UserSegmentationStore as Store
} from 'modules/userManager';

import { initialState } from './constants';
import './UserSegmentationApi';

import { initialFiltersValue } from '../deepSearch/constants';

import Request from '@hotmart/request';

class UserSegmentationService {
  // #region actions
  @action
  async getSegmentations() {
    const initialSegmentations = [this.emptySegmentation];

    try {
      const response = await Request('segmentations.get');
      const segmentations = [...initialSegmentations, ...response];

      this.setData({ segmentations });
      this.setSegmentationMap(segmentations);
    } catch (error) {
      UiService.handleError(error);
      this.setData({ segmentations: initialSegmentations });
    }
  }

  @action
  async create(segmentation) {
    try {
      const response = await Request('segmentation.create', {
        ...segmentation,
        name: trim(segmentation.name)
      });

      await this.getSegmentations();

      Object.assign(segmentation, response);
      this.setActiveSegmentation(
        segmentation,
        this.segmentationsMap[segmentation.id]
      );

      UiService.setAlertSuccess('done');
      return segmentation;
    } catch (error) {
      UiService.handleError(error);
      this.closeNewSegmentationModal();

      return Promise.reject(error);
    }
  }

  @action
  async createEmptySegmentation(name) {
    const response = await this.create({ name, search: { filters: [] } });

    DeepSearchService.openDeepSearch();
    this.performSearch({ showLoading: false });

    return response;
  }

  @action
  async duplicateSegmentation(name) {
    const { segmentation } = Store;
    const search = segmentation.id
      ? segmentation.search
      : DeepSearchService.searchParameters;
    const deepSearchBehavior =
      !segmentation.id && search.filters.length
        ? 'closeDeepSearch'
        : 'openDeepSearch';

    const response = await this.create({ name, search });

    this.setData({ duplicate: false });
    DeepSearchService[deepSearchBehavior]();

    this.performSearch({ showLoading: false });
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    UiService.smoothScroll(null, 500);

    return response;
  }

  @action
  async save() {
    try {
      const { segmentation, segmentations } = Store;
      const search = DeepSearchService.searchParameters;

      await Request('segmentation.update', { ...segmentation, search });

      const segmentationsUpdated = segmentations.map((segm) => {
        if (segm.id === segmentation.id) {
          segm.search = { ...search };
        }

        return segm;
      });

      DeepSearchService.setDirty(false);

      this.setData({ segmentations: segmentationsUpdated });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @action
  async saveSegmentation() {
    const isDeepSearchValid = DeepSearchService.validateDeepSearch();

    if (isDeepSearchValid) {
      try {
        DeepSearchService.setSaveLoading(true);

        await this.save();

        DeepSearchService.setSaveLoading(false);
        DeepSearchService.setDone('saveDone', false);

        this.performSearch({ showLoading: false });
      } catch (error) {
        UiService.handleError(error);

        DeepSearchService.setSaveLoading(false);
      }
    }
  }

  @action
  async rename(name) {
    const { segmentation: originalSegmentation, segmentations } = Store;
    const segmentation = { ...originalSegmentation, name: trim(name) };

    try {
      this.setData({ renameLoading: true, segmentation });
      await Request('segmentation.rename', {
        id: segmentation.id,
        name: segmentation.name
      });

      const segmentationsUpdated = segmentations.map((segm) => {
        if (segm.id === segmentation.id) {
          segm.name = segmentation.name;
        }

        return segm;
      });

      this.setData({
        renameLoading: false,
        segmentations: segmentationsUpdated
      });
      this.setDone('renameDone');
    } catch (error) {
      UiService.handleError(error);

      this.setData({
        renameDone: false,
        renameLoading: false,
        segmentation: originalSegmentation
      });

      return Promise.reject(error);
    }
  }

  @action
  async delete(id) {
    try {
      this.setData({ deleteLoading: true });
      await Request('segmentation.delete', { id });

      UiService.setAlertSuccess('done');
      this.setData({ deleteLoading: false });
    } catch (error) {
      UiService.handleError(error);

      return Promise.reject(error);
    }
  }

  @action
  performSearch(options = {}) {
    DeepSearchService.deepSearch(Object.assign(options, { scroll: false }));
  }
  // #endregion

  // #region getters
  get emptySegmentation() {
    return cloneDeep(initialFiltersValue);
  }

  get hasSegmentation() {
    return Store.segmentation && Store.segmentation.id;
  }

  get isDirty() {
    if (!this.hasSegmentation) {
      return false;
    }

    const { isDirty: isDeepSearchDirty } = DeepSearchService;
    const { segmentation } = Store;

    const segmentationWasEmpty = !segmentation.search.filters.length;
    const deepSearchIsFilled = Boolean(
      DeepSearchService.deepSearchObject.totalFilterCount
    );
    const emptySegmentationWasCleaned =
      segmentationWasEmpty && !deepSearchIsFilled;

    return isDeepSearchDirty && !emptySegmentationWasCleaned;
  }
  // #endregion

  // #region setters
  setSegmentationMap(segmentations) {
    if (!segmentations) {
      segmentations = [];
    }

    this.segmentationsMap = segmentations.reduce(
      (map, { id }, index) => ({ ...map, [id]: index }),
      {}
    );
  }

  @action
  clear() {
    this.setData({ ...initialState });
  }

  @action
  setData(data = {}) {
    extendObservable(Store, { ...data });
  }

  @action
  async setSegmentationByIndex(active = 0) {
    const segmentation = Store.segmentations[active];
    const allTabIndex = 0;

    if (Store.active === allTabIndex) {
      this.updateSegmentation(DeepSearchService.deepSearchObject);
    }

    await this.setActiveSegmentation(segmentation, active);
  }

  @action
  async setActiveSegmentation(segmentation, active) {
    this.setData({ active });
    this.setSegmentation(segmentation);
    await this.setDeepSearchParams(segmentation);
  }

  @action
  setSegmentation(segmentation) {
    if (!(segmentation && segmentation.id)) {
      segmentation = cloneDeep(initialFiltersValue);
    }

    this.setData({ segmentation });
  }

  @action
  async setDeepSearchParams(segmentation) {
    segmentation.id
      ? DeepSearchService.setSearchParameters(segmentation.search)
      : await DeepSearchService.setDeepSearch(segmentation);
  }

  @action
  updateSegmentation(segmentation) {
    const { segmentations } = Store;
    const oldSegmentations =
      segmentations && segmentations.length > 1
        ? Store.segmentations.slice(1)
        : [];

    const newSegmentations = [segmentation, ...oldSegmentations];

    this.setData({ segmentations: newSegmentations });
  }

  @action
  openNewSegmentationModal(options) {
    this.setData({ newSegmentationModal: true, ...options });
  }

  @action
  openDuplicateModal() {
    this.openNewSegmentationModal({ duplicate: true });
  }

  @action
  closeNewSegmentationModal() {
    this.setData({ newSegmentationModal: false, duplicate: false });
  }

  @action
  setDone(doneAttr) {
    this.setData({ [doneAttr]: true });

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    setTimeout(() => this.setData({ [doneAttr]: false }), 1000);
  }
  // #endregion
}

export default new UserSegmentationService();
